import { DecimalPipe } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Company } from 'src/app/core/companies.service';
import { DialogService } from 'src/app/core/dialog.service';
import { ErrorService, SplittypayError } from 'src/app/core/error.service';
import { COMPANIES_TABLE_COLUMNS } from 'src/app/shared/constants/operationals';
import { CompaniesContractDialogComponent } from '../companies-contract-dialog/companies-contract-dialog.component';
import { UtilsService } from 'src/app/core/utils.service';
import { CompaniesPaymentMethodsDialogComponent } from '../companies-payment-methods-dialog/companies-payment-methods-dialog.component';
import { CompaniesArchiveDialogComponent } from '../companies-archive-dialog/companies-archive-dialog.component';
import { CompaniesEditDialogComponent } from '../companies-edit-dialog/companies-edit-dialog.component';
import { CompaniesSalesNetworkDialogComponent } from '../companies-sales-network-dialog/companies-sales-network-dialog.component';
import { CompaniesProductCategoryDialogComponent } from '../companies-product-category-dialog/companies-product-category-dialog.component';
import { CompaniesSettingsDialogComponent } from '../companies-settings-dialog/companies-settings-dialog.component';
import { CompaniesSubdomainDialogComponent } from '../companies-subdomain-dialog/companies-subdomain-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { CompaniesAllowedStepsDialogComponent } from '../companies-allowed-steps-dialog/companies-allowed-steps-dialog.component';
import { CompaniesTicketDialogComponent } from '../companies-ticket-dialog/companies-ticket-dialog.component';
import { NavigationService } from 'src/app/core/navigation.service';
import { CompaniesAgreementDialogComponent } from '../companies-agreement-dialog/companies-agreement-dialog.component';
import { CompaniesToggleDialogComponent } from '../companies-toggle-dialog/companies-toggle-dialog.component';
import { CompaniesSalesReferenceEmailDialogComponent } from '../companies-sales-reference-email-dialog/companies-sales-reference-email-dialog.component';
import { ProductCategory, ProductCategoryService } from 'src/app/core/product-category.service';
import { map, Observable } from 'rxjs';

@Component({
  selector: 'app-companies-data-table',
  templateUrl: './companies-data-table.component.html',
  styleUrls: ['./companies-data-table.component.scss'],
})
export class CompaniesDataTableComponent {
  @Input() companies: Company[] = [];
  @Output() refresh: EventEmitter<void> = new EventEmitter<void>();
  @Output() scrolledToEnd: EventEmitter<void> = new EventEmitter<void>();
  displayed_columns: string[] = COMPANIES_TABLE_COLUMNS;
  product_categories: ProductCategory[] = [];

  constructor(
    private decimal_pipe: DecimalPipe,
    private dialog_service: DialogService,
    private error_service: ErrorService,
    private utils_service: UtilsService,
    private translate: TranslateService,
    private navigation_service: NavigationService,
    private product_category_service: ProductCategoryService,
  ) {}

  ngOnInit(): void {
    this.listProductCategories();
  }

  listProductCategories(): void {
    this.product_category_service.listAllProductCategories().subscribe({
      next: response => {
        this.product_categories = response.data;
      },
    });
  }

  onContractClick(company: Company): void {
    this.dialog_service
      .openDialog(CompaniesContractDialogComponent, { company })
      ?.afterClosed()
      .subscribe(dialog_result => {
        if (dialog_result && dialog_result.error) {
          const processed_error: SplittypayError = dialog_result.error;
          this.error_service.processApiError(processed_error.code, processed_error.message);
        }
      });
  }

  onEnableDisableClick(company: Company): void {
    this.dialog_service
      .openDialog(CompaniesToggleDialogComponent, { company })
      ?.afterClosed()
      .subscribe(dialog_result => {
        if (dialog_result.success) {
          this.refresh.emit();
        }
        if (dialog_result.error) {
          const processed_error: SplittypayError = dialog_result.error;
          this.error_service.processApiError(processed_error.code, processed_error.message);
        }
      });
  }

  onAllowedStepsClick(company: Company): void {
    this.dialog_service
      .openDialog(CompaniesAllowedStepsDialogComponent, { company })
      ?.afterClosed()
      .subscribe(dialog_result => {
        if (dialog_result && dialog_result.success) {
          this.refresh.emit();
        }

        if (dialog_result && dialog_result.error) {
          const processed_error: SplittypayError = dialog_result.error;
          this.error_service.processApiError(processed_error.code, processed_error.message);
        }
      });
  }

  onPaymentMethodsClick(company: Company): void {
    this.dialog_service
      .openDialog(CompaniesPaymentMethodsDialogComponent, { company_id: company.company_id })
      ?.afterClosed()
      .subscribe(dialog_result => {
        if (dialog_result && dialog_result.success) {
          this.refresh.emit();
        }

        if (dialog_result && dialog_result.error) {
          const processed_error: SplittypayError = dialog_result.error;
          this.error_service.processApiError(processed_error.code, processed_error.message);
        }
      });
  }

  onScroll(event: Event) {
    const element = event.target as HTMLElement;
    const end_offset = 200;
    if (element) {
      if (element.offsetHeight + element.scrollTop >= element.scrollHeight - end_offset) {
        this.scrolledToEnd.emit();
      }
    }
  }

  onArchiveClick(company: Company): void {
    this.dialog_service
      .openDialog(CompaniesArchiveDialogComponent, {
        company_id: company.company_id,
        company_business_name: company.business_name,
      })
      ?.afterClosed()
      .subscribe(dialog_result => {
        if (dialog_result && dialog_result.success) {
          this.refresh.emit();
        }

        if (dialog_result && dialog_result.error) {
          const processed_error: SplittypayError = dialog_result.error;
          this.error_service.processApiError(processed_error.code, processed_error.message);
        }
      });
  }

  onEditClick(company: Company): void {
    this.dialog_service
      .openDialog(CompaniesEditDialogComponent, { company })
      ?.afterClosed()
      .subscribe(dialog_result => {
        if (dialog_result && dialog_result.success) {
          this.refresh.emit();
        }

        if (dialog_result && dialog_result.error) {
          const processed_error: SplittypayError = dialog_result.error;
          this.error_service.processApiError(processed_error.code, processed_error.message);
        }
      });
  }

  onSetSalesNetworkIdClick(company: Company): void {
    this.dialog_service
      .openDialog(CompaniesSalesNetworkDialogComponent, {
        company_id: company.company_id,
        sales_network_id: company.sales_network_id,
      })
      ?.afterClosed()
      .subscribe(dialog_result => {
        if (dialog_result && dialog_result.success) {
          this.refresh.emit();
        }

        if (dialog_result && dialog_result.error) {
          const processed_error: SplittypayError = dialog_result.error;
          this.error_service.processApiError(processed_error.code, processed_error.message);
        }
      });
  }

  onSetProductCategoryClick(company: Company): void {
    this.dialog_service
      .openDialog(CompaniesProductCategoryDialogComponent, {
        company_id: company.company_id,
        product_category: company.product_category,
      })
      ?.afterClosed()
      .subscribe(dialog_result => {
        if (dialog_result && dialog_result.success) {
          this.refresh.emit();
        }

        if (dialog_result && dialog_result.error) {
          const processed_error: SplittypayError = dialog_result.error;
          this.error_service.processApiError(processed_error.code, processed_error.message);
        }
      });
  }

  onSetAgreementClick(company: Company): void {
    this.dialog_service
      .openDialog(CompaniesAgreementDialogComponent, { company_id: company.company_id, agreement: company.agreement })
      ?.afterClosed()
      .subscribe(dialog_result => {
        if (dialog_result && dialog_result.success) {
          this.refresh.emit();
        }

        if (dialog_result && dialog_result.error) {
          const processed_error: SplittypayError = dialog_result.error;
          this.error_service.processApiError(processed_error.code, processed_error.message);
        }
      });
  }

  onSettingsClick(company: Company): void {
    this.dialog_service
      .openDialog(CompaniesSettingsDialogComponent, { company })
      ?.afterClosed()
      .subscribe(dialog_result => {
        if (dialog_result && dialog_result.success) {
          this.refresh.emit();
        }

        if (dialog_result && dialog_result.error) {
          const processed_error: SplittypayError = dialog_result.error;
          this.error_service.processApiError(processed_error.code, processed_error.message);
        }
      });
  }

  onSetCustomDomainClick(company: Company): void {
    this.dialog_service
      .openDialog(CompaniesSubdomainDialogComponent, { company_id: company.company_id })
      ?.afterClosed()
      .subscribe(dialog_result => {
        if (dialog_result && dialog_result.success) {
          this.refresh.emit();
        }

        if (dialog_result && dialog_result.error) {
          const processed_error: SplittypayError = dialog_result.error;
          this.error_service.processApiError(processed_error.code, processed_error.message);
        }
      });
  }

  onDetailsClick(company: Company): void {
    this.navigation_service.toCompanyDetails(company.company_id as string);
  }

  getRevenuesRange(company: Company): string {
    if (company) {
      if (company.min_revenue != 999999999) {
        const min_revenue: string | null = this.formatNumber(company.min_revenue);
        const max_revenue: string | null = this.formatNumber(company.max_revenue);
        return company ? `${min_revenue}€ - ${max_revenue}€` : '-';
      } else {
        return this.translate.instant('STORES.MORE_REVENUE');
      }
    }
    return '-';
  }

  onTicketClick(company: Company): void {
    this.dialog_service
      .openDialog(CompaniesTicketDialogComponent, { company })
      ?.afterClosed()
      .subscribe(dialog_result => {
        if (dialog_result && dialog_result.success) {
          this.refresh.emit();
        }

        if (dialog_result && dialog_result.error) {
          const processed_error: SplittypayError = dialog_result.error;
          this.error_service.processApiError(processed_error.code, processed_error.message);
        }
      });
  }

  getCompanyStatusBackgroundClass(company?: Company): { [key: string]: boolean } {
    if (!company) return {};
    return this.utils_service.getCompanyStatusBackgroundClass(company);
  }

  getCompanyStatusLabel(company: Company): string {
    if (!company?.status) return '';
    return this.utils_service.getCompanyStatusLabel(company.status, company.is_partially_enabled);
  }

  getCompanyProductCategoryLabel(company: Company): string {
    const product_category = company.product_category;
    if (!product_category) {
      return '-';
    }
    const category = this.product_categories.find(c => c.product_category_id === product_category);
    return category ? category.name : '-';
  }

  formatNumber(digits: number): string | null {
    return this.decimal_pipe.transform(digits, '0.2-2', 'it-IT');
  }

  isSalesNetworkIDEditable(company: Company): boolean {
    return !company?.is_sales_network_id_editing_disabled;
  }

  onSetSalesReferenceEmailClick(company: Company): void {
    this.dialog_service
      .openDialog(CompaniesSalesReferenceEmailDialogComponent, {
        company_id: company.company_id,
        sales_reference_email: company.sales_reference_email,
      })
      ?.afterClosed()
      .subscribe(dialog_result => {
        if (dialog_result && dialog_result.success) {
          this.refresh.emit();
        }

        if (dialog_result && dialog_result.error) {
          const processed_error: SplittypayError = dialog_result.error;
          this.error_service.processApiError(processed_error.code, processed_error.message);
        }
      });
  }
}
